import { Fragment, useEffect, useId, useRef, useState } from "react";
import dayjs from "dayjs";
import { Mui, useTheme } from "@leap4/frontend";
import { Bar, CartesianGrid, ComposedChart, LabelProps, Line, ReferenceArea, Tooltip, XAxis, YAxis, useActiveTooltipCoordinate, useActiveTooltipLabel } from "recharts";
import { numericFontFamily } from "../../../theme/colors";
import { RunTimer } from "../reporting/base/runTimer";
import { DashboardLoadingChart } from "./DashboardLoadingChart";

const { Box, Button, Paper, Tooltip: MuiTooltip, Typography } = Mui;

export interface IChartMetric {
    key: string;
    label: string;
    color: string;
    type: "line" | "bar";
    yAxisId: string;
    decimals: number;
    description?: string;
    /** The paying event: the default owner of the right axis. */
    primary?: boolean;
}

/** One event's open attribution window; `partialKey` is the bucket it opens part-way through. */
export interface IMaturingWindow {
    key: string;
    label: string;
    color: string;
    days: number;
    from: string;
    partialKey: string | null;
    fullKey: string | null;
}

export interface IChartComparison {
    key: string;
    label: string;
    color: string;
}

/** One bucket. A metric is null when no row exists for the bucket, which draws as a gap rather than a zero. */
export type IChartPoint = { key: string; label: string } & Record<string, string | number | null>;

interface IPerformanceChartProps {
    data: IChartPoint[];
    metrics: IChartMetric[];
    visibleKeys: string[];
    onToggle: (key: string) => void;
    onReset: () => void;
    comparisons: IChartComparison[];
    /** The series the comparisons replay, in its own axis. */
    comparisonOf: IChartMetric | null;
    maturingFromKey: string | null;
    maturingWindows: IMaturingWindow[];
    loading?: boolean;
    height: number;
    compact?: boolean;
    summary: string;
    /** The event that owns the right axis; the page shares it with the cards and the legend. */
    focusKey: string | null;
    onFocusChange: (key: string) => void;
    /** Series the viewer turned on; the legend shows these. `visibleKeys` may add a hover preview on top. */
    enabledKeys?: string[];
}

/**
 * Width of the chart box, applied a frame after the observer fires. Re-rendering inside the observer's own callback
 * is what makes the browser report a ResizeObserver loop.
 */
const useBoxWidth = () => {
    const [element, setElement] = useState<HTMLDivElement | null>(null);
    const [width, setWidth] = useState(0);
    useEffect(() => {
        if (!element) return undefined;
        let frame = 0;
        const observer = new ResizeObserver(([entry]) => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => setWidth(Math.floor(entry.contentRect.width)));
        });
        observer.observe(element);
        return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
        };
    }, [element]);
    return [setElement, width, element] as const;
};

interface IActiveDay {
    key: string;
    x: number;
}

/**
 * Lifts the hovered or keyboard-focused day out of recharts, so the tooltip renders as our own overlay. The recharts
 * Tooltip measures itself with a ResizeObserver and, under the legacy ReactDOM.render root, re-renders inside that
 * callback, which is the ResizeObserver loop the dev overlay reported.
 */
const ActiveDayReporter = ({ onChange }: { onChange: (day: IActiveDay | null) => void }) => {
    const label = useActiveTooltipLabel();
    const x = useActiveTooltipCoordinate()?.x;
    useEffect(() => {
        onChange(label !== undefined && x !== undefined ? { key: String(label), x } : null);
    }, [label, x, onChange]);
    return null;
};

const CHART_MARGIN_TOP = 26;
/** Approximate width of one 10px tick character; each event column is sized to its own longest tick. */
const EVENT_TICK_CHAR_PX = 6;
/** Gap between an event column's left edge and its tick text; the colour dot sits over the same spot. */
const EVENT_TICK_MARGIN = 4;
const X_AXIS_HEIGHT = 30;
/** How close the cursor must come to an event line, in pixels, to focus it. */
const LINE_HOVER_PX = 14;
const EVENTS_AXIS = "__events";

/** The smallest axis maximum in four steps of 1, 2 or 5 × 10^n that holds `max`; an empty series gets 0–4. */
const niceMax = (max: number, decimals: number): number => {
    if (max <= 0) return 4;
    const raw = Math.max(max / 4, decimals > 0 ? 0.1 : 1);
    const magnitude = 10 ** Math.floor(Math.log10(raw));
    const step = [1, 2, 5, 10].map((f) => f * magnitude).find((candidate) => candidate >= raw - 1e-9) ?? 10 * magnitude;
    return step * 4;
};

/** A dot in the event's colour above its axis column, standing in for the name. */
const axisDot =
    (color: string) =>
    ({ viewBox }: LabelProps) =>
        viewBox && "x" in viewBox && viewBox.x !== undefined && viewBox.y !== undefined ? (
            <circle cx={viewBox.x + EVENT_TICK_MARGIN + 4} cy={viewBox.y - 10} r={3.5} fill={color} />
        ) : (
            <g />
        );

/** Compact ticks for the narrow event columns: 200K, 1.5K, 0.4. */
const formatCompactAxisValue = (value: number): string => {
    const trim = (n: number) => String(Number(n.toFixed(1)));
    if (value >= 1_000_000) return `${trim(value / 1_000_000)}M`;
    if (value >= 1_000) return `${trim(value / 1_000)}K`;
    return trim(value);
};

const formatAxisValue = (value: number): string => {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
    return Number.isInteger(value) ? String(value) : value.toFixed(1);
};

export const formatMetricValue = (value: number, decimals: number): string =>
    value.toLocaleString("en-US", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

interface ITooltipProps {
    active?: boolean;
    payload?: Array<{ payload?: IChartPoint }>;
    metrics: IChartMetric[];
    comparisons: IChartComparison[];
    comparisonOf: IChartMetric | null;
    maturingWindows: IMaturingWindow[];
}

const ChartTooltip = ({ active, payload, metrics, comparisons, comparisonOf, maturingWindows }: ITooltipProps) => {
    const point = payload?.[0]?.payload;
    if (!active || !point) return null;
    const missing = metrics.every((m) => point[m.key] === null || point[m.key] === undefined);

    return (
        <Paper elevation={3} sx={{ p: 1.5, minWidth: 180 }}>
            <Typography variant="caption" sx={{ color: "text.secondary", mb: 0.75, display: "block" }}>
                {point.label}
            </Typography>
            {missing ? (
                <Typography variant="caption">No data recorded for this day</Typography>
            ) : (
                metrics.map((m) => <TooltipRow key={m.key} color={m.color} label={m.label} value={point[m.key]} decimals={m.decimals} />)
            )}
            {comparisonOf &&
                comparisons.map((c) => (
                    <TooltipRow key={c.key} color={c.color} label={`${comparisonOf.label} · ${c.label}`} value={point[c.key]} decimals={comparisonOf.decimals} dashed />
                ))}
            {maturingWindows
                .filter((w) => point.key === w.partialKey || (!!w.fullKey && point.key >= w.fullKey))
                .map((w) => (
                    <Typography key={w.key} variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
                        {point.key === w.partialKey ? `${w.label} maturing from ${dayjs(w.from).format("MMM D")}` : `${w.label} still maturing`}
                    </Typography>
                ))}
        </Paper>
    );
};

const TooltipRow = ({ color, label, value, decimals, dashed }: { color: string; label: string; value: unknown; decimals: number; dashed?: boolean }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.25 }}>
        <Box
            aria-hidden="true"
            sx={dashed ? { width: 10, height: 0, borderTop: `2px dashed ${color}`, flexShrink: 0 } : { width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }}
        />
        <Typography variant="caption" sx={{ color: "text.secondary", flex: 1 }}>
            {label}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 600, fontFamily: numericFontFamily, fontVariantNumeric: "tabular-nums" }}>
            {typeof value === "number" ? formatMetricValue(value, decimals) : "—"}
        </Typography>
    </Box>
);

/** The DSP DashboardChart: impressions on the left axis; each event on its own scale, with only the focused event's axis drawn on the right. */
export const PerformanceChart = (props: IPerformanceChartProps) => {
    const { data, metrics, visibleKeys, onToggle, onReset, comparisons, comparisonOf, maturingFromKey, maturingWindows, loading, height, compact, summary, focusKey, onFocusChange, enabledKeys = visibleKeys } = props;
    const theme = useTheme();
    const patternId = `maturing-${useId().replace(/:/g, "")}`;
    const [boxRef, boxWidth, boxElement] = useBoxWidth();
    const [activeDay, setActiveDay] = useState<IActiveDay | null>(null);
    // Recharts delivers mouse moves a frame late; one landing after the pointer left must not move focus.
    const pointerInside = useRef(false);

    const visible = metrics.filter((m) => visibleKeys.includes(m.key));
    const axisColor = theme.palette.text.secondary;
    const gridColor = theme.palette.divider;
    const labelByKey = new Map(data.map((p) => [p.key, p.label]));
    const lastKey = data.length ? data[data.length - 1].key : null;
    const showDots = data.length <= 45;
    // Every event line stays mounted and toggles with `hide`: a newly mounted recharts Line registers from an effect and
    // draws a commit late, which flashed the chart on every hover preview.
    const lineMetrics = metrics.filter((m) => m.type === "line");
    const lines = visible.filter((m) => m.type === "line");
    // One right axis, the focused event's; the other events keep their own scale but draw without an axis.
    const focused = lines.find((m) => m.key === focusKey) ?? lines.find((m) => m.primary) ?? lines[0] ?? null;
    const activePoint = activeDay ? data.find((p) => p.key === activeDay.key) : undefined;
    const bar = visible.find((m) => m.type === "bar") ?? null;

    // Each event is drawn against its own nice maximum on a shared 0–1 axis; the one right axis shows the focused
    // event's real values. Recharts' own multi-axis layout pushes the visible axis off-canvas past hidden ones.
    const numeric = (v: unknown): number => (typeof v === "number" ? v : 0);
    const scaleMax = new Map(
        lineMetrics.map((m) => {
            const replayed = comparisonOf?.key === m.key ? comparisons.flatMap((c) => data.map((p) => numeric(p[c.key]))) : [];
            return [m.key, niceMax(Math.max(0, ...data.map((p) => numeric(p[m.key])), ...replayed), m.decimals)];
        }),
    );
    const plotData = data.map((p) => {
        const point: IChartPoint = { ...p };
        lineMetrics.forEach((m) => {
            const v = p[m.key];
            point[`__n_${m.key}`] = typeof v === "number" ? v / (scaleMax.get(m.key) ?? 1) : null;
        });
        if (comparisonOf?.type === "line") {
            comparisons.forEach((c) => {
                const v = p[c.key];
                point[`__n_${c.key}`] = typeof v === "number" ? v / (scaleMax.get(comparisonOf.key) ?? 1) : null;
            });
        }
        return point;
    });
    // Each turned-on event gets its own axis column; a hover preview draws its line without one, so hovering never
    // resizes the plot. Only visible axes are mounted: recharts lays out hidden right axes as if they took space.
    const axisLines = lines.filter((m) => enabledKeys.includes(m.key));
    const eventAxisId = (key: string) => `__axis_${key}`;
    const eventAxes = axisLines.map((m) => {
        const max = scaleMax.get(m.key) ?? 4;
        const ticks = [0, 1, 2, 3, 4].map((n) => (max / 4) * n);
        const longest = Math.max(...ticks.map((t) => formatCompactAxisValue(t).length));
        return { metric: m, max, ticks, width: EVENT_TICK_MARGIN + longest * EVENT_TICK_CHAR_PX + 2 };
    });
    const eventAxesWidth = eventAxes.reduce((sum, a) => sum + a.width, 0);
    const baseAxisId = bar?.yAxisId ?? (axisLines.length ? eventAxisId(axisLines[0].key) : EVENTS_AXIS);

    // Focus follows the cursor: the event line nearest the pointer on the hovered day, within LINE_HOVER_PX.
    const focusNearestLine = (index: number, cursorY: number) => {
        const point = plotData[index];
        if (!point) return;
        const plotHeight = height - CHART_MARGIN_TOP - X_AXIS_HEIGHT;
        const hits = lines
            .map((m) => {
                const v = point[`__n_${m.key}`];
                return { key: m.key, distance: typeof v === "number" ? Math.abs(CHART_MARGIN_TOP + (1 - v) * plotHeight - cursorY) : Infinity };
            })
            .filter((hit) => hit.distance <= LINE_HOVER_PX)
            .sort((a, b) => a.distance - b.distance);
        // Overlapping lines (e.g. several at zero) give no unique answer, so focus stays where it is.
        if (!hits.length || hits.some((hit) => hit.key === focused?.key && hit.distance - hits[0].distance < 1)) return;
        if (hits.length > 1 && hits[1].distance - hits[0].distance < 1) return;
        onFocusChange(hits[0].key);
    };

    return (
        <Box sx={{ width: "100%", minWidth: 0 }}>
            {maturingWindows.length > 0 && (
                // Inline items under text-wrap: balance, so rows split evenly (3 + 2, not 4 + 1) whatever the label lengths.
                <Box sx={{ textAlign: "center", textWrap: "balance", mb: 0.5 }}>
                    {maturingWindows.map((w, i) => (
                        <Fragment key={w.key}>
                            {i > 0 && " "}
                            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, mx: 1, my: 0.25, whiteSpace: "nowrap" }}>
                                <Box component="span" aria-hidden="true" sx={{ width: 14, height: 4, borderRadius: "2px", bgcolor: w.color }} />
                                <Typography variant="caption" color="text.secondary">
                                    {w.label} maturing from {dayjs(w.from).format("MMM D")} · {w.days}-day window
                                </Typography>
                            </Box>
                        </Fragment>
                    ))}
                </Box>
            )}
            {loading && !data.length ? (
                <DashboardLoadingChart height={height} spinner label={<>Loading performance… <RunTimer /></>} />
            ) : !visible.length ? (
                <Box sx={{ height, display: "grid", placeItems: "center" }}>
                    <Typography color="text.secondary">Select a metric below to view performance</Typography>
                </Box>
            ) : (
                <Box role="region" aria-label="Performance chart, scroll horizontally on smaller screens" tabIndex={0} sx={{ width: "100%", overflowX: "auto" }}>
                    <Typography component="p" sx={visuallyHidden}>
                        {summary}
                    </Typography>
                    <Box
                        ref={boxRef}
                        onMouseEnter={() => (pointerInside.current = true)}
                        onMouseLeave={() => (pointerInside.current = false)}
                        sx={{ position: "relative", minWidth: compact ? 520 : 600 + eventAxesWidth, height }}
                    >
                    {loading && (
                        <Box sx={{ position: "absolute", inset: 0, zIndex: 1, bgcolor: "background.paper" }}>
                            <DashboardLoadingChart height={height} spinner label={<>Loading performance… <RunTimer /></>} />
                        </Box>
                    )}
                    {activeDay && activePoint && (
                        <Box
                            sx={{
                                position: "absolute",
                                top: CHART_MARGIN_TOP,
                                zIndex: 2,
                                pointerEvents: "none",
                                ...(activeDay.x > boxWidth / 2 ? { right: boxWidth - activeDay.x + 12 } : { left: activeDay.x + 12 }),
                            }}
                        >
                            <ChartTooltip active payload={[{ payload: activePoint }]} metrics={visible} comparisons={comparisons} comparisonOf={comparisonOf} maturingWindows={maturingWindows} />
                        </Box>
                    )}
                        {boxWidth > 0 && (
                            <ComposedChart
                                width={boxWidth}
                                height={height}
                                data={plotData}
                                margin={{ top: CHART_MARGIN_TOP, right: 8, bottom: 0, left: 0 }}
                                barGap={4}
                                barCategoryGap="25%"
                                accessibilityLayer
                                // Recharts calls this a frame late, after React has cleared event.currentTarget; measure against our own box.
                                onMouseMove={(state, event) => {
                                    const index = Number(state.activeTooltipIndex);
                                    if (pointerInside.current && boxElement && state.isTooltipActive && Number.isInteger(index)) focusNearestLine(index, event.clientY - boxElement.getBoundingClientRect().top);
                                }}
                            >
                                <defs>
                                    <pattern id={patternId} width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                                        <rect width="8" height="8" fill={theme.palette.action.hover} />
                                        <line x1="0" y1="0" x2="0" y2="8" stroke={theme.palette.text.disabled} strokeWidth="1.5" strokeOpacity="0.45" />
                                    </pattern>
                                </defs>
                                <CartesianGrid yAxisId={baseAxisId} strokeDasharray="4 4" stroke={gridColor} vertical={false} />
                                <XAxis
                                    dataKey="key"
                                    height={X_AXIS_HEIGHT}
                                    tickFormatter={(key: string) => labelByKey.get(key) ?? key}
                                    tick={{ fontSize: 12, fill: axisColor, fontFamily: numericFontFamily }}
                                    axisLine={{ stroke: gridColor }}
                                    tickLine={false}
                                    minTickGap={compact ? 48 : 28}
                                />
                                {bar && (
                                    <YAxis
                                        yAxisId={bar.yAxisId}
                                        orientation="left"
                                        domain={[0, "auto"]}
                                        allowDecimals={bar.decimals > 0}
                                        width={76}
                                        tick={{ fontSize: 11, fill: axisColor, fontFamily: numericFontFamily }}
                                        axisLine={false}
                                        tickLine={false}
                                        label={{ value: bar.label, position: "top", fill: axisColor, fontSize: 11, fontFamily: numericFontFamily, offset: 12 }}
                                        tickFormatter={formatAxisValue}
                                    />
                                )}
                                {eventAxes.map(({ metric: m, max, ticks, width }) => {
                                    return (
                                        <YAxis
                                            key={m.key}
                                            yAxisId={eventAxisId(m.key)}
                                            orientation="right"
                                            domain={[0, max]}
                                            ticks={ticks}
                                            allowDataOverflow
                                            width={width}
                                            tick={{ fontSize: 10, fill: m.key === focused?.key ? theme.palette.text.primary : axisColor, fontFamily: numericFontFamily }}
                                            axisLine={false}
                                            tickLine={false}
                                            tickSize={0}
                                            tickMargin={EVENT_TICK_MARGIN}
                                            tickFormatter={formatCompactAxisValue}
                                            label={{ position: "top", content: axisDot(m.color) }}
                                        />
                                    );
                                })}
                                <YAxis yAxisId={EVENTS_AXIS} orientation="right" domain={[0, 1]} allowDataOverflow hide width={0} />
                                {maturingFromKey && lastKey && (
                                    <ReferenceArea
                                        yAxisId={baseAxisId}
                                        x1={maturingFromKey}
                                        x2={lastKey}
                                        fill={`url(#${patternId})`}
                                        fillOpacity={1}
                                        stroke="none"
                                        ifOverflow="extendDomain"
                                    />
                                )}
                                {/* Empty content: recharts only draws the cursor; the tooltip is the overlay above. */}
                                <Tooltip content={() => null} cursor={{ fill: theme.palette.action.hover }} />
                                <ActiveDayReporter onChange={setActiveDay} />
                                {bar && <Bar dataKey={bar.key} name={bar.label} yAxisId={bar.yAxisId} fill={bar.color} fillOpacity={0.45} radius={[4, 4, 0, 0]} maxBarSize={36} isAnimationActive={false} />}
                                {lineMetrics.map((m) => (
                                        <Line
                                            key={m.key}
                                            hide={!visibleKeys.includes(m.key)}
                                            type="monotone"
                                            dataKey={`__n_${m.key}`}
                                            name={m.label}
                                            yAxisId={EVENTS_AXIS}
                                            stroke={m.color}
                                            strokeWidth={m.key === focused?.key ? 2.5 : 1.5}
                                            strokeOpacity={m.key === focused?.key ? 1 : 0.55}
                                            dot={showDots ? { r: 2.5, fill: m.color, fillOpacity: m.key === focused?.key ? 1 : 0.55, strokeWidth: 0 } : false}
                                            activeDot={{ r: 5, stroke: theme.palette.background.paper, strokeWidth: 2 }}
                                            isAnimationActive={false}
                                        />
                                ))}
                                {comparisonOf &&
                                    comparisons.map((c) => (
                                        <Line
                                            key={c.key}
                                            type="monotone"
                                            dataKey={comparisonOf.type === "bar" ? c.key : `__n_${c.key}`}
                                            name={`${comparisonOf.label} · ${c.label}`}
                                            yAxisId={comparisonOf.type === "bar" ? comparisonOf.yAxisId : EVENTS_AXIS}
                                            stroke={c.color}
                                            strokeWidth={1.75}
                                            strokeDasharray="5 4"
                                            dot={false}
                                            activeDot={{ r: 4 }}
                                            isAnimationActive={false}
                                        />
                                    ))}
                                {maturingWindows.length > 0 && <YAxis yAxisId="__window" domain={[0, 1]} hide width={0} />}
                                {/* One thin strip per event along the top, in its colour; faint over a part-open month. */}
                                {lastKey &&
                                    maturingWindows.map((w, i) => {
                                        const y2 = 1 - i * 0.03;
                                        const y1 = y2 - 0.015;
                                        return (
                                            <Fragment key={w.key}>
                                                {w.partialKey && <ReferenceArea yAxisId="__window" x1={w.partialKey} x2={w.partialKey} y1={y1} y2={y2} fill={w.color} fillOpacity={0.3} stroke="none" />}
                                                {w.fullKey && <ReferenceArea yAxisId="__window" x1={w.fullKey} x2={lastKey} y1={y1} y2={y2} fill={w.color} fillOpacity={0.9} stroke="none" />}
                                            </Fragment>
                                        );
                                    })}
                            </ComposedChart>
                        )}
                    </Box>
                </Box>
            )}

            <Box role="group" aria-label="Performance metrics" sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexWrap: "wrap", columnGap: 0.5, pt: 0.5 }}>
                {metrics.map((m) => {
                    const active = enabledKeys.includes(m.key);
                    return (
                        <MuiTooltip key={m.key} title={m.description ?? m.label} describeChild>
                            <Button
                                size="small"
                                aria-pressed={active}
                                onClick={() => onToggle(m.key)}
                                onMouseEnter={() => m.type === "line" && onFocusChange(m.key)}
                                onFocus={() => m.type === "line" && onFocusChange(m.key)}
                                sx={{
                                    minWidth: 0,
                                    minHeight: 32,
                                    px: 0.75,
                                    gap: 0.5,
                                    fontSize: 12,
                                    fontWeight: 400,
                                    color: active ? m.color : "text.secondary",
                                    opacity: active ? 1 : 0.7,
                                    "&:hover": { bgcolor: "transparent", opacity: 1 },
                                    "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
                                }}
                            >
                                <Box component="span" aria-hidden="true" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: "currentColor", flexShrink: 0 }} />
                                {m.label}
                            </Button>
                        </MuiTooltip>
                    );
                })}
                <Button size="small" onClick={onReset} sx={{ minWidth: 0, minHeight: 32, ml: 1, px: 0.75, fontSize: 11, fontWeight: 400, color: "text.secondary" }}>
                    Reset to defaults
                </Button>
            </Box>
        </Box>
    );
};

const visuallyHidden = {
    position: "absolute",
    // Strings, not numbers: sx reads 1 as 100%.
    width: "1px",
    height: "1px",
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap",
} as const;
